import { createAction, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  changeActiveSmartView,
  changeActiveSmartViewFulfilledActionHandler,
  createNewSmartView,
  createNewSmartViewFulfilledActionHandler,
  createNewSmartViewRejectedActionHandler,
  DashboardSmartViewsSliceState,
  deleteSmartView,
  deleteSmartViewFulfilledActionHandler,
  deleteSmartViewRejectedActionHandler,
  discardSmartViewChanges,
  discardSmartViewChangesActionHandler,
  discardSmartViewChangesAsync,
  discardSmartViewChangesAsyncFulfilledActionHandler,
  expandActiveSmartViewCategory,
  expandActiveSmartViewCategoryActionHandler,
  renameSmartView,
  renameSmartViewFulfilledActionHandler,
  saveSmartViewConfiguration,
  saveSmartViewConfigurationFulfilledActionHandler,
  saveSmartViewConfigurationRejectedActionHandler,
  setReduxStateFromSmartViewConfiguration,
  setResetGridStack,
  setResetGridStackActionHandler,
  setShouldDisplayUnsavedChangesPopUp,
  setShouldDisplayUnsavedChangesPopUpFulfilledHandler,
  setSmartViews,
  setSmartViewsActionHandler,
  setSmartViewsCategories,
  setSmartViewsCategoriesActionHandler,
  smartViewsAdapter,
  smartViewsCategoriesAdapter,
  smartViewsInitialState,
  switchActiveSmartViewFromPanel,
  toggleCollapsedStateForSmartViewsCategoryFulfilledActionHandler,
  toggleIsOpenStateForSmartViewsCategory,
  toggleSmartViewsPanelCollapseFulfilledHandler,
  toggleSmartViewsPanelCollapseRejectedHandler,
  toggleSmartViewsPanelIsCollapsed,
  updateSmartViewsSearchText,
  updateSmartViewsSearchTextActionHandler,
} from './dashboards.smart.views.slice';
import {
  filtersAdapter,
  filtersInitialState,
  FiltersState,
  resetFilters,
  resetFiltersActionHandler,
  setFilters,
  setFiltersActionHandler,
} from './dashboard.filters.slice';
import {
  dashboardUiInitialSliceState,
  DashboardUiSliceState,
  disableLiveView,
  disableLiveViewActionHandler,
  setDashboardConfiguration,
  setDashboardConfigurationActionHandler,
  toggleExportButtonSpinner,
  toggleExportButtonSpinnerHandler,
  toggleFiltersPanelActiveState,
  toggleFiltersPanelActiveStateActionHandler,
  toggleFullscreenActiveState,
  toggleFullscreenActiveStateActionHandler,
  toggleHasWidgetsInErrorState,
  toggleHasWidgetsInErrorStateHandler,
  toggleLiveViewActiveState,
  toggleLiveViewActiveStateActionHandler,
  toggleSettingsPanelActiveState,
  toggleSettingsPanelActiveStateActionHandler,
  toggleWidgetDetailsPanelActiveState,
  toggleWidgetDetailsPanelActiveStateActionHandler,
} from './dashboards.ui.slice';
import {
  markWidgetInactive,
  markWidgetInactiveActionHandler,
  resetWidgets,
  resetWidgetsActionHandler,
  setRefreshWidgets,
  setRefreshWidgetsActionHandler,
  setWidgets,
  setWidgetsActionHandler,
  setWidgetsCategories,
  setWidgetsCategoriesActionHandler,
  toggleIsOpenStateForWidgetCategory,
  toggleIsOpenStateForWidgetCategoryFulfilledActionHandler,
  updateSettingsPanelWidgetsActive2State,
  updateSettingsPanelWidgetsActive2StateActionHandler,
  updateWidget,
  updateWidgetActionHandler,
  updateWidgetsConfigs,
  updateWidgetsConfigsActionHandler,
  updateWidgetsSearchText,
  updateWidgetsSearchTextActionHandler,
  widgetCategoryEntityAdapter,
  widgetsAdapter,
  widgetsInitialState,
  WidgetsSliceState,
} from './dashboards.widgets.slice';
import { RootState } from '../../redux/store';
import {
  GenericViewConfig,
  GetUserConfigForDashboardResponse,
} from '@nirvana/shared/dtos/types/generic-dashboards/gz-console';
import { DashboardsReduxActions } from '../types/types';
import { DashboardReduxWidget } from './types';
import { EntitiesHelper } from '../utils/entities.helper';
import { DashboardSmartView } from '../gzDashboardSmartViewsList/types';
import { UnifiedFilterConfig } from '../gzDashboardFilters/types';

export interface DashboardAppState {
  filtersSlice: FiltersState;
  uiSlice: DashboardUiSliceState;
  widgetsSlice: WidgetsSliceState;
  viewsSlice: DashboardSmartViewsSliceState;
}

export const dashboardAppInitialState: DashboardAppState = {
  viewsSlice: smartViewsInitialState,
  filtersSlice: filtersInitialState,
  widgetsSlice: widgetsInitialState,
  uiSlice: dashboardUiInitialSliceState,
};

/*
 * We need this because the true initial state is invalid for the GzDashboard framework;
 * the component would be in an error state if it was rendered based on it.
 * We can't also use individual actions because some rely on results from other (async) actions.
 * Therefore, we need a way to insert an entire initialState,
 * and since the response is directly from backend, it's already validated.
 */
export const setBulkInitialState = createAction<GetUserConfigForDashboardResponse>(
  DashboardsReduxActions.SET_BULK_INITIAL_STATE
);
export const setBulkInitialStateActionHandler = (
  state: DashboardAppState,
  action: PayloadAction<GetUserConfigForDashboardResponse>
) => {
  // Preparing data
  const data = action.payload;
  const activeViewConfig = data.smartViews.find(view => view.id === data.activeViewProps.id) as GenericViewConfig;
  const reduxWidgets: Array<DashboardReduxWidget> = EntitiesHelper.getWidgetsForRedux(
    data.availableWidgets,
    data.smartViews,
    activeViewConfig
  );
  const reduxSmartViews: Array<DashboardSmartView> = EntitiesHelper.getSmartViewsForRedux(
    data.smartViews,
    data.activeViewProps.id
  );
  const reduxFilters: Array<UnifiedFilterConfig> = EntitiesHelper.getFiltersForRedux(
    data.filterConfigs,
    activeViewConfig.filters
  );

  state.uiSlice.configuration = { ...data.uiConfig, dashboardType: data.dashboardType };
  smartViewsAdapter.setAll(state.viewsSlice.views, reduxSmartViews);
  widgetsAdapter.setAll(state.widgetsSlice.widgets, reduxWidgets);
  widgetCategoryEntityAdapter.setAll(state.widgetsSlice.categories, data.widgetsCategories);
  smartViewsCategoriesAdapter.setAll(state.viewsSlice.categories, data.smartViewsCategories);
  filtersAdapter.setAll(state.filtersSlice.filters, reduxFilters);
  state.widgetsSlice.refreshNecessary = false;
  state.uiSlice.ui.settingsPanelActive = false;
  state.viewsSlice.gridStackFirstInitOfWidgets = true;
  switchActiveSmartViewFromPanel(state, data.activeViewProps.id);
  setReduxStateFromSmartViewConfiguration(state);
};

export const dashboardAppSlice = createSlice({
  name: 'dashboardsApp',
  initialState: dashboardAppInitialState,
  reducers: {},
  extraReducers: builder => {
    builder
      // INITIAL STATE
      .addCase(setBulkInitialState, setBulkInitialStateActionHandler)
      // UI SLICE
      .addCase(toggleLiveViewActiveState, toggleLiveViewActiveStateActionHandler)
      .addCase(toggleFullscreenActiveState, toggleFullscreenActiveStateActionHandler)
      .addCase(toggleSettingsPanelActiveState, toggleSettingsPanelActiveStateActionHandler)
      .addCase(toggleWidgetDetailsPanelActiveState, toggleWidgetDetailsPanelActiveStateActionHandler)
      .addCase(toggleFiltersPanelActiveState, toggleFiltersPanelActiveStateActionHandler)
      .addCase(setDashboardConfiguration, setDashboardConfigurationActionHandler)
      .addCase(disableLiveView, disableLiveViewActionHandler)
      .addCase(toggleExportButtonSpinner, toggleExportButtonSpinnerHandler)
      .addCase(toggleHasWidgetsInErrorState, toggleHasWidgetsInErrorStateHandler)
      // FILTERS
      .addCase(setFilters, setFiltersActionHandler)
      .addCase(resetFilters, resetFiltersActionHandler)
      // SMART VIEWS
      .addCase(
        toggleIsOpenStateForSmartViewsCategory.fulfilled,
        toggleCollapsedStateForSmartViewsCategoryFulfilledActionHandler
      )
      .addCase(expandActiveSmartViewCategory, expandActiveSmartViewCategoryActionHandler)
      .addCase(changeActiveSmartView.fulfilled, changeActiveSmartViewFulfilledActionHandler)
      .addCase(renameSmartView.fulfilled, renameSmartViewFulfilledActionHandler)
      .addCase(updateSmartViewsSearchText, updateSmartViewsSearchTextActionHandler)
      .addCase(setSmartViews, setSmartViewsActionHandler)
      .addCase(setSmartViewsCategories, setSmartViewsCategoriesActionHandler)
      .addCase(saveSmartViewConfiguration.fulfilled, saveSmartViewConfigurationFulfilledActionHandler)
      .addCase(saveSmartViewConfiguration.rejected, saveSmartViewConfigurationRejectedActionHandler)
      .addCase(setResetGridStack, setResetGridStackActionHandler)
      .addCase(discardSmartViewChangesAsync.fulfilled, discardSmartViewChangesAsyncFulfilledActionHandler)
      .addCase(setShouldDisplayUnsavedChangesPopUp.fulfilled, setShouldDisplayUnsavedChangesPopUpFulfilledHandler)
      .addCase(discardSmartViewChanges, discardSmartViewChangesActionHandler)
      .addCase(createNewSmartView.fulfilled, createNewSmartViewFulfilledActionHandler)
      .addCase(createNewSmartView.rejected, createNewSmartViewRejectedActionHandler)
      .addCase(deleteSmartView.fulfilled, deleteSmartViewFulfilledActionHandler)
      .addCase(deleteSmartView.rejected, deleteSmartViewRejectedActionHandler)
      .addCase(toggleSmartViewsPanelIsCollapsed.fulfilled, toggleSmartViewsPanelCollapseFulfilledHandler)
      .addCase(toggleSmartViewsPanelIsCollapsed.rejected, toggleSmartViewsPanelCollapseRejectedHandler)
      // WIDGETS
      .addCase(toggleIsOpenStateForWidgetCategory.fulfilled, toggleIsOpenStateForWidgetCategoryFulfilledActionHandler)
      .addCase(updateWidgetsSearchText, updateWidgetsSearchTextActionHandler)
      .addCase(setRefreshWidgets, setRefreshWidgetsActionHandler)
      .addCase(setWidgets, setWidgetsActionHandler)
      .addCase(setWidgetsCategories, setWidgetsCategoriesActionHandler)
      .addCase(updateSettingsPanelWidgetsActive2State, updateSettingsPanelWidgetsActive2StateActionHandler)
      .addCase(updateWidget, updateWidgetActionHandler)
      .addCase(resetWidgets, resetWidgetsActionHandler)
      .addCase(updateWidgetsConfigs, updateWidgetsConfigsActionHandler)
      .addCase(markWidgetInactive, markWidgetInactiveActionHandler);
  },
});

export const selectDashboardsAppState = (state: RootState) => state.dashboardsApp;

export const dashboardsAppReducer = dashboardAppSlice.reducer;
